/**
 * Sino de notificações do header (C-24) — fiel ao `notif-bell` do protótipo V8 (L942):
 * botão 36×34 com o sino de traço (`ic('bell')` do V8, como o telefone ao lado), badge vermelho com as não lidas ("9+" acima de 9) e painel suspenso.
 *
 * Componente de apresentação: recebe a lista pronta (domínio) e o conjunto de lidas; quem
 * navega e guarda o "lido" é o hook do Header (`useNotificacoesDoSino`).
 *
 * Fecha no Esc (pela pilha do `usePopup`, C-91; foco volta ao sino), ao clicar fora e ao
 * escolher um item. No celular o
 * painel é `fixed` com margem de 16px dos dois lados: ancorado no sino, a 375px ele
 * sairia pela esquerda da tela.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { contarNaoLidas, rotuloDoBadge, type NotificacaoDoSino } from '@/domain/notificacoes/sino';
import { usePopup } from '@/hooks/usePopup';
import { BellIcon } from './BellIcon';
import NotificationPanel from './NotificationPanel';

export interface NotificationBellProps {
  notificacoes: NotificacaoDoSino[];
  lidas: ReadonlySet<string>;
  isLoading: boolean;
  error: Error | null;
  /** Relógio para o "há 12 min"; o hook passa o `useAgora`. */
  agora?: Date;
  onAbrir: (n: NotificacaoDoSino) => void;
  onMarcarTodas: () => void;
}

export default function NotificationBell({
  notificacoes, lidas, isLoading, error, agora, onAbrir, onMarcarTodas,
}: NotificationBellProps) {
  const [aberto, setAberto] = useState(false);
  const [agoraPadrao] = useState(() => new Date());
  const rootRef = useRef<HTMLDivElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const painelId = useId();

  const naoLidas = contarNaoLidas(notificacoes, lidas);
  const badge = rotuloDoBadge(naoLidas);

  // Esc pela pilha de popups (C-91): fecha só o sino quando ele é o de cima e devolve o
  // foco ao botão. Como o menu do usuário, é um painel suspenso: sem prender o Tab e sem
  // puxar o foco ao abrir.
  usePopup({
    aberto,
    aoFechar: () => {
      setAberto(false);
      botaoRef.current?.focus();
    },
    prenderFoco: false,
    gerirFoco: false,
  });

  // Clique fora fecha. Listener só enquanto aberto; o sino vive dentro de `rootRef`, então
  // clicar nele de novo segue para o toggle do botão.
  useEffect(() => {
    if (!aberto) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [aberto]);

  const nomeAcessivel = [
    'Notificações',
    naoLidas > 0 ? `${naoLidas} não ${naoLidas === 1 ? 'lida' : 'lidas'}` : null,
    error ? 'carga incompleta' : null,
  ].filter(Boolean).join(' — ');

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={botaoRef}
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-label={nomeAcessivel}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        aria-controls={aberto ? painelId : undefined}
        title="Notificações"
        className="relative flex h-[34px] w-8 min-[420px]:w-9 items-center justify-center rounded-[9px] border border-jf-border bg-white/5 text-jf-muted transition hover:bg-white/10 hover:text-white"
      >
        <BellIcon size={17} />
        {badge && (
          <span
            data-testid="notif-badge"
            aria-hidden
            className="absolute -right-[5px] -top-[5px] flex h-4 min-w-[16px] items-center justify-center rounded-[9px] border-2 border-jf-surface2 bg-level-emergencia px-1 text-[10px] font-extrabold text-white"
          >
            {badge}
          </span>
        )}
        {/* Falha de carga: marcador próprio, no canto oposto ao badge — com o "2" de dois
            instrumentos e a query de TARPs quebrada, o número sozinho passaria por lista
            completa. Sem lista nenhuma, o sino não pode parecer "nada pendente". */}
        {error && (
          <span
            data-testid="notif-erro"
            aria-hidden
            title="Carga incompleta"
            className="absolute -bottom-[5px] -left-[5px] flex h-4 min-w-[16px] items-center justify-center rounded-[9px] border-2 border-jf-surface2 bg-slate-500 px-1 text-[10px] font-extrabold text-white"
          >
            !
          </span>
        )}
      </button>

      {aberto && (
        <div
          id={painelId}
          role="dialog"
          aria-label="Notificações"
          className="fixed inset-x-4 top-14 z-50 max-h-[70vh] overflow-y-auto rounded-[14px] border border-jf-border bg-jf-surface shadow-[0_16px_40px_rgba(0,0,0,.5)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-11 sm:w-[340px]"
        >
          <NotificationPanel
            notificacoes={notificacoes}
            lidas={lidas}
            isLoading={isLoading}
            error={error}
            agora={agora ?? agoraPadrao}
            onAbrir={(n) => {
              setAberto(false);
              onAbrir(n);
            }}
            onMarcarTodas={onMarcarTodas}
          />
        </div>
      )}
    </div>
  );
}
